import type { PropType } from "vue";
import type { SelectTriggerVariants } from "./variants";
import {
  SelectContent,
  SelectGroup,
  SelectIcon,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectLabel,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";
import { defineComponent } from "vue";
import { forwardEmit } from "@/utils";

import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Icon from "../../ui/icon/Icon";
import { selectContentVariants, selectItemVariants, selectTriggerVariants } from "./variants";

const Trigger = defineComponent({
  name: "SelectTrigger",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    disabled: p.boolean(),
    size: p.string<"sm" | "md" | "lg" | "xs">("md"),
    variant: p.string<SelectTriggerVariants["variant"]>("default"),
    placeholder: p.string(),
    arrowIcon: p.string("lucide:chevron-down"),
    loading: p.boolean(),
  },
  setup(props, { slots, attrs }) {
    // asChild renders the caller's own trigger element (same pattern as
    // Dropdown.Trigger): the variant classes are skipped so it can mirror any
    // other button pixel-for-pixel.
    return () => (
      <SelectTrigger
        as="button"
        asChild={props.asChild}
        disabled={props.disabled}
        class={
          props.asChild
            ? (attrs.class ?? "")
            : cn(
              selectTriggerVariants({
                size: props.size,
                variant: props.variant,
              }),
              attrs.class ?? ""
            )
        }
      >
        {props.asChild
          ? slots.default?.()
          : (
            <>
              <SelectValue placeholder={props.placeholder}>{slots.default?.()}</SelectValue>
              {props.arrowIcon && (
                <SelectIcon asChild>
                  <Icon icon={props.arrowIcon} class="ml-2 h-4 w-4 opacity-50" />
                </SelectIcon>
              )}
            </>
          )}
      </SelectTrigger>
    );
  },
});

const Content = defineComponent({
  name: "SelectContent",
  emits: {
    onCloseAutoFocus: (_e: FocusEvent) => true,
    onEscapeKeyDown: (_e: KeyboardEvent) => true,
    onPointerDownOutside: (_e: PointerEvent) => true,
  },
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    align: p.string<"start" | "end" | "center">("start"),
    alignOffset: p.number(),
    arrowPadding: p.number(),
    avoidCollisions: p.boolean(true),
    collisionBoundary: {
      type: [String, Array] as PropType<Element | Element[]>,
    },
    collisionPadding: p.number(),
    position: p.string<"popper" | "item-aligned">("popper"),
    prioritizePosition: p.boolean(),
    sticky: p.string<"partial" | "always">(),
    hideWhenDetached: p.boolean(),
    side: p.string<"top" | "bottom">(),
    sideOffset: p.number(),
    updatePositionStrategy: p.string<"always" | "optimized">(),
  },
  setup(props, { slots, attrs, emit }) {
    const emits = forwardEmit(emit);

    return () => (
      <SelectPortal>
        <SelectContent
          {...emits}
          align={props.align}
          alignOffset={props.alignOffset}
          arrowPadding={props.arrowPadding}
          as={props.as}
          asChild={props.asChild}
          avoidCollisions={true}
          collisionBoundary={props.collisionBoundary}
          collisionPadding={props.collisionPadding}
          position={props.position}
          prioritizePosition={props.prioritizePosition}
          sticky={props.sticky}
          hideWhenDetached={props.hideWhenDetached}
          side={props.side}
          sideOffset={props.sideOffset}
          updatePositionStrategy={props.updatePositionStrategy}
          class={cn(selectContentVariants(), attrs.class ?? "")}
        >
          <SelectScrollUpButton class="flex h-[25px] cursor-default items-center justify-center bg-popover text-foreground">
            <Icon icon="lucide:chevron-up" class="h-4-w-4" />
          </SelectScrollUpButton>
          <SelectViewport
            class={cn(
              "p-1",
              props.position === "popper" &&
                "h-(--reka-select-trigger-height) w-full min-w-(--reka-select-trigger-width)"
            )}
          >
            {slots.default?.()}
          </SelectViewport>
          <SelectScrollDownButton class="flex h-[25px] cursor-default items-center justify-center bg-popover text-foreground">
            <Icon icon="lucide:chevron-down" class="h-4-w-4" />
          </SelectScrollDownButton>
        </SelectContent>
      </SelectPortal>
    );
  },
});

const Label = defineComponent({
  name: "SelectLabel",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    for: p.string(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <SelectLabel
        for={props.for}
        as={props.as}
        asChild={props.asChild}
        class={cn("px-2 py-1.5 text-sm font-semibold", attrs.class ?? "")}
      >
        {slots.default?.()}
      </SelectLabel>
    );
  },
});

const Item = defineComponent({
  name: "SelectItem",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    disabled: p.boolean(),
    value: p.$string(),
    textValue: p.string(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <SelectItem
        as={props.as}
        asChild={props.asChild}
        disabled={props.disabled}
        value={props.value}
        textValue={props.textValue}
        class={cn(selectItemVariants(), attrs.class ?? "")}
      >
        <span class="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          <SelectItemIndicator>
            <Icon icon="lucide:check" class="h-4 w-4" />
          </SelectItemIndicator>
        </span>
        <SelectItemText>{slots.default?.()}</SelectItemText>
      </SelectItem>
    );
  },
});

const Separator = defineComponent({
  name: "SelectSeparator",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
  },
  setup(props, { attrs }) {
    return () => (
      <SelectSeparator
        as={props.as}
        asChild={props.asChild}
        class={cn("-mx-1 my-1 h-px bg-muted", attrs.class ?? "")}
      />
    );
  },
});

export const Select = {
  Root: SelectRoot,
  Group: SelectGroup,
  Value: SelectValue,
  Trigger,
  Content,
  Label,
  Item,
  Separator,
} as {
  Root: typeof SelectRoot;
  Group: typeof SelectGroup;
  Value: typeof SelectValue;
  Trigger: typeof Trigger;
  Content: typeof Content;
  Label: typeof Label;
  Item: typeof Item;
  Separator: typeof Separator;
};
